import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Send, RotateCcw, Loader2, Mic, MicOff, Volume2, VolumeX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import amyAvatar from "@/assets/amy-avatar.jpg";

const STORAGE_KEY = "amy_chat_v1";
const SESSION_KEY = "amy_session_id";
const VOICE_KEY = "amy_voice_consent";

function loadMessages(): UIMessage[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as UIMessage[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveMessages(messages: UIMessage[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // ignore quota
  }
}

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

type SpeechRecognitionCtor = new () => {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult:
    | ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void)
    | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function getSpeechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const WELCOME =
  "Hi, I'm Amy — sales assistant at Vending Machine Free. Ask me anything about placing a free vending machine at your business, and I can line up a callback within 24 hours whenever you're ready.";

const SUGGESTIONS = [
  "Do you cover my state?",
  "What machine types do you offer?",
  "How does the free placement work?",
  "I'd like a machine for my office.",
];

interface AmyChatProps {
  variant?: "widget" | "page";
}

export function AmyChat({ variant = "widget" }: AmyChatProps) {
  const [hydrated, setHydrated] = useState(false);
  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const recognitionRef = useRef<InstanceType<SpeechRecognitionCtor> | null>(null);
  const spokenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setInitialMessages(loadMessages());
    setSessionId(getOrCreateSessionId());
    try {
      setVoiceEnabled(window.localStorage.getItem(VOICE_KEY) === "1");
    } catch {
      // ignore
    }
    setHydrated(true);
  }, []);

  const speechAvailable =
    typeof window !== "undefined" &&
    !!getSpeechRecognition() &&
    typeof window.speechSynthesis !== "undefined";

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/public/chat",
        headers: (): Record<string, string> =>
          sessionId ? { "x-amy-session": sessionId } : {},
      }),
    [sessionId],
  );

  const { messages, sendMessage, status, error, setMessages } = useChat({
    id: "amy",
    messages: initialMessages,
    transport,
    onError: (err) => {
      console.error("[amy][chat] error", err);
    },
  });

  useEffect(() => {
    if (!hydrated) return;
    saveMessages(messages);
  }, [messages, hydrated]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  useEffect(() => {
    if (status === "ready") inputRef.current?.focus();
  }, [status]);

  // Speak new assistant messages when voice is enabled.
  useEffect(() => {
    if (!voiceEnabled || typeof window === "undefined") return;
    if (status !== "ready") return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    if (spokenIdsRef.current.has(last.id)) return;
    const text = last.parts
      .map((p) => (p.type === "text" ? p.text : ""))
      .join(" ")
      .trim();
    if (!text) return;
    spokenIdsRef.current.add(last.id);
    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 1;
      utter.pitch = 1;
      utter.lang = "en-US";
      synth.speak(utter);
    } catch (e) {
      console.warn("[amy][voice] speak failed", e);
    }
  }, [messages, status, voiceEnabled]);

  const isBusy = status === "submitted" || status === "streaming";

  const submit = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isBusy) return;
      if (voiceEnabled && typeof window !== "undefined") {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* noop */
        }
      }
      void sendMessage({ text: trimmed });
      setInput("");
    },
    [isBusy, sendMessage, voiceEnabled],
  );

  const reset = useCallback(() => {
    setMessages([]);
    saveMessages([]);
    setInput("");
    spokenIdsRef.current.clear();
    if (typeof window !== "undefined") {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* noop */
      }
    }
    inputRef.current?.focus();
  }, [setMessages]);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(VOICE_KEY, next ? "1" : "0");
      } catch {
        /* noop */
      }
      if (!next && typeof window !== "undefined") {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* noop */
        }
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch {
            /* noop */
          }
        }
        setListening(false);
      }
      return next;
    });
  }, []);

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognition();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const first = e.results?.[0]?.[0];
      const transcript = first?.transcript ?? "";
      if (transcript) submit(transcript);
    };
    rec.onerror = (e) => {
      console.warn("[amy][voice] recognition error", e);
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      console.warn("[amy][voice] start failed", e);
      setListening(false);
    }
  }, [submit]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* noop */
      }
    }
    setListening(false);
  }, []);

  return (
    <section
      aria-label="Chat with Amy, AI sales assistant"
      className={cn(
        "flex flex-col bg-background text-foreground",
        variant === "widget" ? "h-full" : "h-[calc(100vh-4rem)] max-h-[820px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <img
            src={amyAvatar}
            alt="Portrait of Amy, AI sales assistant for Vending Machine Free"
            width={36}
            height={36}
            loading="lazy"
            className="h-9 w-9 rounded-full object-cover ring-2 ring-[#2F5D8A]/20"
          />
          <div>
            <p className="text-sm font-semibold leading-tight">Amy</p>
            <p className="text-xs text-muted-foreground leading-tight">
              Sales assistant · replies in seconds
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {speechAvailable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleVoice}
              className="h-8 px-2 text-xs text-muted-foreground"
              aria-pressed={voiceEnabled}
              aria-label={
                voiceEnabled
                  ? "Disable voice input and spoken replies"
                  : "Enable voice input and spoken replies (uses your browser's microphone and speech synthesis)"
              }
              title={
                voiceEnabled
                  ? "Voice mode on — click to disable"
                  : "Enable voice mode (mic + spoken replies)"
              }
            >
              {voiceEnabled ? (
                <Volume2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <VolumeX className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              )}
              Voice
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            className="h-8 px-2 text-xs text-muted-foreground"
            aria-label="Start a new conversation with Amy"
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            New
          </Button>
        </div>
      </div>

      {/* Transcript */}
      <div
        ref={scrollerRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Conversation with Amy"
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      >
        {messages.length === 0 ? (
          <>
            <AssistantBubble>
              <p>{WELCOME}</p>
            </AssistantBubble>
            <div
              className="flex flex-wrap gap-2 pt-1"
              role="group"
              aria-label="Suggested first questions"
            >
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s)}
                  className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs text-foreground hover:bg-muted"
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        ) : (
          messages.map((m) => <MessageView key={m.id} message={m} />)
        )}
        {status === "submitted" ? (
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            role="status"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Amy is thinking…
          </div>
        ) : null}
        {error ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            Something went wrong. Please try again.
          </div>
        ) : null}
      </div>

      {voiceEnabled && (
        <div className="border-t border-border bg-muted/30 px-4 py-2 text-[11px] text-muted-foreground">
          Voice mode is on. Your microphone is used only when you click the mic
          button, and replies are read aloud by your browser. Turn off any time.
        </div>
      )}

      {/* Composer */}
      <form
        className="border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit(input);
        }}
        aria-label="Send a message to Amy"
      >
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
            placeholder="Ask Amy anything about free vending machines…"
            rows={1}
            aria-label="Your message to Amy"
            className="min-h-[42px] max-h-32 resize-none text-sm"
            disabled={isBusy}
          />
          {voiceEnabled && speechAvailable && (
            <Button
              type="button"
              size="icon"
              variant={listening ? "default" : "outline"}
              onClick={listening ? stopListening : startListening}
              disabled={isBusy}
              aria-pressed={listening}
              aria-label={
                listening
                  ? "Stop voice input"
                  : "Start voice input using your microphone"
              }
              className="h-10 w-10 shrink-0"
            >
              {listening ? (
                <MicOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Mic className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          )}
          <Button
            type="submit"
            size="icon"
            disabled={isBusy || !input.trim()}
            aria-label="Send message to Amy"
            className="h-10 w-10 shrink-0"
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </div>
        <p className="mt-2 text-[10px] leading-snug text-muted-foreground">
          Amy is an AI assistant. For contract terms, a real teammate will
          follow up.
        </p>
      </form>
    </section>
  );
}

function MessageView({ message }: { message: UIMessage }) {
  const isUser = message.role === "user";
  return (
    <div
      className={cn("flex", isUser ? "justify-end" : "justify-start")}
      role="article"
      aria-label={isUser ? "You said" : "Amy said"}
    >
      <div className={cn("max-w-[85%] space-y-2")}>
        {message.parts.map((part, i) => {
          if (part.type === "text") {
            return isUser ? (
              <UserBubble key={i}>{part.text}</UserBubble>
            ) : (
              <AssistantBubble key={i}>
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-[#2F5D8A] underline underline-offset-2"
                        target={
                          href && href.startsWith("http") ? "_blank" : undefined
                        }
                        rel={
                          href && href.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        {children}
                      </a>
                    ),
                    p: ({ children }) => (
                      <p className="whitespace-pre-wrap">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc pl-4">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-4">{children}</ol>
                    ),
                  }}
                >
                  {part.text}
                </ReactMarkdown>
              </AssistantBubble>
            );
          }
          if (part.type && String(part.type).startsWith("tool-")) {
            const p = part as {
              type: string;
              state?: string;
              output?: { ok?: boolean; message?: string };
              errorText?: string;
            };
            const isDone = p.state === "output-available";
            const isError =
              p.state === "output-error" || (isDone && p.output?.ok === false);
            const label = isError
              ? "Couldn't save the lead"
              : isDone
                ? "Lead saved — team will call within 24 hours"
                : "Sending your details to the team…";
            return (
              <div
                key={i}
                role="status"
                aria-live="polite"
                className={cn(
                  "rounded-md border px-3 py-2 text-xs",
                  isError
                    ? "border-destructive/40 bg-destructive/10 text-destructive"
                    : "border-[#2F5D8A]/30 bg-[#2F5D8A]/5 text-[#2F5D8A]",
                )}
              >
                <div className="flex items-center gap-2">
                  {!isDone && !isError ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : (
                    <span className="inline-block h-2 w-2 rounded-full bg-current" aria-hidden="true" />
                  )}
                  <span className="font-medium">{label}</span>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

function AssistantBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground">
      {children}
    </div>
  );
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl rounded-br-sm bg-[#2F5D8A] px-3 py-2 text-sm text-white shadow-sm whitespace-pre-wrap">
      {children}
    </div>
  );
}
